import React, { useEffect, useState } from "react";
import { FaCommentDots } from "react-icons/fa";
import { FiPaperclip, FiMic } from "react-icons/fi";
import { getPanelChatByPanelId, getPanelChatDocument, sendPanelChatMessage } from "app/apis/panels";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faEye } from "@fortawesome/free-solid-svg-icons";
import { useAppContext } from "app/contexts/AppContext";
import EmployeeProfile from "app/views/common_component/EmployeeProfile";
import { useTranslation } from "react-i18next";

const ChatTab = () => {
    const { selectedPanel } = useAppContext()
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [chatDetail, setChatDetail] = useState({});
    const [files, setFiles] = useState([]);
    const [change, setChange] = useState(false);
    const employee_id = sessionStorage.getItem('employee_id');
    const [documentURL, setDocumentURL] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const { t } = useTranslation();
    const [payLoad, setPayLoad] = useState({
        panel: selectedPanel?.uu_id,
        created_by: employee_id,
        message: '',
        files: [],
    });

    useEffect(() => {
        if (!selectedPanel?.uu_id) return;
        const fetchChatDetail = async () => {
            try {
                const response = await getPanelChatByPanelId(selectedPanel?.uu_id);
                setChatDetail(response?.data);
            } catch (error) {
                console.error("Error has been occurred:", error);
                setError(error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchChatDetail();
    }, [selectedPanel?.uu_id, change]);

    const handleFileChange = (e) => {
        const selectedFiles = Array.from(e.target.files);
        setFiles((prevFiles) => [...prevFiles, ...selectedFiles]);
        setPayLoad((prev) => ({
            ...prev,
            files: [...prev.files, ...selectedFiles],
        }));
    };

    const removeFile = (index) => {
        const newFiles = files.filter((_, i) => i !== index);
        setFiles(newFiles);
        setPayLoad((prev) => ({
            ...prev,
            files: newFiles,
        }));
    };

    const handleSend = async () => {
        if (!payLoad.message && files.length === 0) return;

        const formData = new FormData();
        formData.append("panel", selectedPanel?.uu_id);
        formData.append("created_by", payLoad.created_by);
        formData.append("message", payLoad.message);

        files.forEach((file) => {
            formData.append("files", file);
        });

        try {
            const response = await sendPanelChatMessage(formData);

            if (response.data) {
                const newChatMessage = response.data.panel_chat;

                setChatDetail((prev) => ({
                    ...prev,
                    panel_chat: [...prev.panel_chat, newChatMessage],
                }));
                setChange(!change);

                setPayLoad({
                    panel: selectedPanel?.uu_id,
                    created_by: employee_id,
                    message: '',
                    files: [],
                });
                setFiles([]);
            }
        } catch (error) {
            console.error("Error sending message:", error);
            setError("Failed to send message. Please try again.");
        }
    };

    const handleView = async (id) => {
        setIsLoading(true);
        try {
            const response = await getPanelChatDocument(id, { responseType: "blob" });
            if (response.status >= 200 && response.status < 300) {
                const blob = new Blob([response.data], { type: response.headers["content-type"] || "application/pdf" });
                const url = window.URL.createObjectURL(blob);
                setDocumentURL(url);
                setIsModalOpen(true);
            } else {
                console.error("Server error:", response.status, response.statusText);
                setError("Failed to fetch document. Please try again.");
            }
        } catch (error) {
            console.error("Network error:", error);
            setError("Failed to fetch document. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleDownload = async (id) => {
        setIsDownloading(true);
        try {
            const response = await getPanelChatDocument(id, { responseType: "blob" });

            if (response.status >= 200 && response.status < 300) {
                const blob = new Blob([response.data], { type: "application/octet-stream" });
                const contentDisposition = response.headers["content-disposition"];
                const fileName = contentDisposition
                    ? contentDisposition.split("filename=")[1]?.split(";")[0]?.replace(/"/g, "")
                    : "document.pdf";

                const url = window.URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", fileName);
                document.body.appendChild(link);
                link.click();

                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            } else {
                console.error("Server error:", response.status, response.statusText);
                setError("Failed to download document. Please try again.");
            }
        } catch (error) {
            console.error("Network error:", error);
            setError("Failed to download document. Please try again.");
        } finally {
            setIsDownloading(false);
        }
    };


    return (
        <div className="relative flex flex-col h-[70vh]">
            {/* Scrollable chat area */}
            <div className="flex-1 p-4 bg-gray-50 overflow-y-auto">
                {chatDetail?.panel_chat?.map((chat, index) => (
                    <div key={index} className="flex items-start gap-2 mb-4">
                        <div className="w-full bg-white">
                            <EmployeeProfile request={chat?.created_by} />
                            <p className="bg-white p-2 rounded shadow">{chat?.message}</p>
                            {chatDetail?.panel_chat_documents
                                ?.filter((doc) => doc.panelchat.uu_id === chat.uu_id)
                                .map((doc, i) => (
                                    <div key={i} className="card__file">
                                        <div className="card__file-info">
                                            <div className="card__file-name">
                                                <span>{doc.document_type}</span>
                                            </div>
                                            <div className="flex gap-2">
                                                <button className="text-gray-500 hover:text-green-500" onClick={() => handleView(doc.uu_id)}>
                                                    <FontAwesomeIcon icon={faEye} />
                                                </button>
                                                <button className="text-gray-500 hover:text-green-500" onClick={() => handleDownload(doc.uu_id)}>
                                                    <FontAwesomeIcon icon={faDownload} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                        </div>
                    </div>
                ))}

                {/* Uploaded files preview */}
                {files.length > 0 && (
                    <div className="p-4 bg-white border-t">
                        {files.map((file, index) => (
                            <div key={index} className="flex items-center justify-between bg-gray-200 p-2 mb-2 rounded">
                                <span className="text-gray-700">{file.name}</span>
                                <button
                                    type="button"
                                    onClick={() => removeFile(index)}
                                    className="text-red-500"
                                    title="Remove file"
                                >
                                    x
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Fixed input area at the bottom */}
            {!selectedPanel?.closed && (
                <div className="p-4 bg-white flex items-center border-t">
                    <label className="p-2 cursor-pointer">
                        <FiPaperclip />
                        <input
                            type="file"
                            accept=".pdf, image/*"
                            className="hidden"
                            onChange={handleFileChange}
                            multiple
                        />
                    </label>
                    <input
                        type="text"
                        // placeholder="Type your message here..."
                        placeholder={t('Type your message here...')}
                        className="flex-1 p-2 border rounded"
                        value={payLoad.message}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleSend();
                            }
                        }}
                        onChange={(e) => setPayLoad((prev) => ({ ...prev, message: e.target.value }))}
                    />
                    {/* <button className="p-2 bg-black text-white rounded ml-2" onClick={handleSend}>Send</button> */}
                    <button className="p-2 bg-black text-white rounded ml-2" onClick={handleSend}>{t('Send')}</button>
                </div>
            )}
            {isModalOpen && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50" style={{ zIndex: 1000 }}>
                    <div className="bg-white p-6 rounded-lg shadow-lg w-3/4 h-3/4 relative">
                        <button onClick={() => setIsModalOpen(false)} className="absolute top-2 right-2 text-gray-600 hover:text-red-500">
                            ✖
                        </button>
                        {/* <h2 className="text-[22] font-semibold mb-2">Document Preview</h2> */}
                        <h2 className="text-[22] font-semibold mb-2">{t('Document Preview')}</h2>
                        {isLoading ? (
                            // <p>Loading...</p>
                            <p>{t('Loading...')}</p>
                        ) : documentURL ? (
                            <iframe src={documentURL} className="w-full h-[95%] border rounded-lg"></iframe>
                        ) : (
                            // <p>Failed to load document</p>
                            <p>{t('Failed to load document')}</p>
                        )}
                    </div>
                </div>
            )}
        </div>

    );
};

export default ChatTab;